import React from "react";
import { submitMedikreditClaim } from "../../mediKreditapi";

const pad = (n) => String(n).padStart(2, "0");

function formatDateYYYYMMDD(dateStr) {
  if (!dateStr) return "";
  return String(dateStr).slice(0, 10).replace(/-/g, "");
}

// Local time → "HHMMSS" (no UTC conversion)
function toHHMMSSLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const hh = pad(d.getHours());
  const mm = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  return `${hh}${mm}${ss}`;
}

function extractDatesAndTimes(line, fallbackInvoiceDate) {
  const startISO = line.appointmentCustomStart || line.appointmentStartSet || null;
  const endISO   = line.appointmentCustomEnd   || line.appointmentEndSet   || null;
  const dateOnly = line.appointmentDateCustom || line.appointmentDateSet || fallbackInvoiceDate || "";

  let stDate = "", endDate = "", stTime = "", endTime = "";

  if (startISO || endISO) {
    const s = startISO ? new Date(startISO) : null;
    const e = endISO ? new Date(endISO) : null;

    stDate  = s ? startISO.slice(0, 10) : (dateOnly || "");
    endDate = e ? endISO.slice(0, 10)   : (stDate || dateOnly || "");

    stTime  = s ? toHHMMSSLocal(startISO) : "000000";
    endTime = e ? toHHMMSSLocal(endISO)   : stTime || "000000";
  } else if (dateOnly) {
    stDate = endDate = dateOnly.slice(0, 10);
    stTime = "000000";
    endTime = "000000";
  }

  return {
    st_dt: formatDateYYYYMMDD(stDate),
    st_tm: stTime,
    end_dt: formatDateYYYYMMDD(endDate),
    end_tm: endTime,
  };
}

function parseCodeListMaybeSemicolon(strOrArr) {
  if (Array.isArray(strOrArr)) return strOrArr.filter(Boolean);
  if (!strOrArr) return [];
  return String(strOrArr)
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean);
}

function getCd1Array(line) {
  // Prefer explicit arrays if present
  if (Array.isArray(line.icd10Primary)) return line.icd10Primary.filter(Boolean);
  if (Array.isArray(line.icd10Primaries)) return line.icd10Primaries.filter(Boolean);
  if (Array.isArray(line.icd101)) return line.icd101.filter(Boolean);

  // Back-compat: single string or semi-colon list from icd101
  if (typeof line.icd101 === "string") {
    return parseCodeListMaybeSemicolon(line.icd101);
  }
  return [];
}

function getCd2Array(line) {
  if (Array.isArray(line.icd10Secondary)) return line.icd10Secondary.filter(Boolean);
  if (Array.isArray(line.icd102)) return line.icd102.filter(Boolean);
  if (line.icd102) return parseCodeListMaybeSemicolon(line.icd102);
  return [];
}

export async function SubmitClaim({
  practice,
  account,
  selectedPatient,
  invoice,
  claimtxn,
}) {
  const practiceInfo = {
    sp_bhf: practice.practiceNumber,
    sp_hpc: invoice.practitioner.hpcsa,
    grp_prac: practice.practiceNumber,
    tx_nbr: claimtxn,
    msg_fmt: "13",
    tx_cd: "21",
  };

  const iodRef = String(selectedPatient?.patientIODClaimRefNumber ?? "")
  .replace(/[^a-zA-Z0-9/]/g, "") // keep only alphanumeric + /
  .split("/")
  .slice(0, 3) // keep only first 3 parts = 2 slashes
  .join("/");

  const medicalAidInfo = {
    plan: selectedPatient.patientMediKreditPlanNumber03,
    ch_id: selectedPatient.patientMedicalAidNumber || iodRef,
    sname: selectedPatient.patientLastName,
    inits: selectedPatient.patientInitials,
  };

  const IODInfo = {
    is_IOD: account.accountIODFund ? "Y" : "N",
    wca_nbr: account.accountIODEmployerWCARegNum,
    ins_ref_nbr: account.accountIODClaimRefNumber,
    dt_of_acdnt: formatDateYYYYMMDD(account.accountIODDateOfInjury),
    pre_iss_tx: "", // Not provided in current data model
  };
console.log("IOD Info:", IODInfo);  


  const financialInfo = {
    totalMedAidAmount: invoice.totalMedicalAidAmount,
  };

  const patientInfo = {
  dep_cd: selectedPatient.patientMedicalAidDependantCode,
  dob: formatDateYYYYMMDD(selectedPatient.patientDOB),
  id_nbr: selectedPatient.patientIdNumber,

  // 🔹 Always uppercase and safely handle null/undefined
  fname: (selectedPatient.patientFirstName || "").toUpperCase(),
  sname: (selectedPatient.patientLastName || "").toUpperCase(),

  gend: selectedPatient.patientGender,
  rlnship: selectedPatient.patientRelationship,
  status: invoice.outPatient ? "O" : "I",
  placeOfService: invoice.placeOfService,

  // 🔹 Initials also uppercase
  int: (selectedPatient.patientInitials || "").toUpperCase(),
};


  // --- NEW: Case info comes from invoice.case (not inPatientCase) ---
  const casePrimaries =
    Array.isArray(invoice.case?.icd101)
      ? invoice.case.icd101.filter(Boolean)
      : invoice.case?.icd101
      ? [invoice.case.icd101]
      : [];
  const caseSecondaries =
    Array.isArray(invoice.case?.icd102) ? invoice.case.icd102.filter(Boolean) : [];

  const caseInfo = {
    ref_bhf: invoice.case?.referringDoctorBHF || "",
    ref_hpcsa: invoice.case?.referringDoctorHPCSA || "",
    auth_nbr: invoice.case?.authorizationNumber || "",
    auth_by: invoice.case?.authorizedBy || "",
    cd1: casePrimaries,   // array of primaries (stg_01)
    cd2: caseSecondaries, // array of secondaries (stg_02)
  };

 // Build transaction lines
let baseLineNumber = 1;
const transactionInfo = [];
const lines = Array.isArray(invoice.lines) ? invoice.lines : [];
const fallbackInvoiceDate = invoice.invoiceDate || "";

for (let i = 0; i < lines.length; i++) {
  const line = lines[i];

  let lineDescription = String(line.description ?? "");
  let lineTariffCode = String(line.tariffCode ?? "");
  const lineQuantity = Number(line.quantity ?? 1);

  const cd1Array = getCd1Array(line);
  const cd2Array = getCd2Array(line);

  const { st_dt, st_tm, end_dt, end_tm } = extractDatesAndTimes(line, fallbackInvoiceDate);

  const lineAuthNumber = caseInfo.auth_nbr || "";

  let modifiers = [];
  if (line.modifier) {
    modifiers = Array.isArray(line.modifier)
      ? line.modifier.filter(Boolean)
      : [line.modifier];
  }

  const hasNegativeModifier = modifiers.some((m) =>
    typeof m === "object" ? Number(m.rateChange) < 0 : false
  );

  const basePrice = hasNegativeModifier
    ? Number(line.medAidAmount ?? 0)
    : Number(line.medAidAmount ?? line.originalRate ?? 0);

  // If NAPPI line, tariff code must always be "0201"
  let nappiCode = null;
  let nappiQty = null;
  if (line.type === "nappi" || line.nappiCode || line.selectedNappi) {
    if (line.selectedNappi?.description) {
      lineDescription = String(line.selectedNappi.description);
    }
    lineTariffCode = "0201";
    nappiCode = String(line.nappiCode || line.selectedNappi?.nappiCode || line.selectedNappi?.code || "").slice(-9);
    nappiQty = Number(line.quantity ?? 1).toFixed(2);
  }

  const baseLineId = String(baseLineNumber);

  const baseLine = {
    lin_num: baseLineId,
    pay_adv: "P",
    tp: "2",
    cdg_set: "02",
    auth_nbr: lineAuthNumber,
    descr: lineDescription,
    process_tp: "01",
    tar_cd: lineTariffCode,
    qty: "1",
    units: lineQuantity,
    unit_tp: "06",
    ...(nappiCode ? { nappi_cd: nappiCode, nappi_qty: nappiQty } : {}),
    st_dt,
    st_tm,
    end_dt,
    end_tm,
    cd1: cd1Array,
    cd2: cd2Array,
    tp_ind: "01",
    bhf_nbr: practiceInfo.sp_bhf,
    hpc_nbr: invoice.practitioner.hpcsa,
    tar_ind: "03",
    price: basePrice,
    gross: basePrice,
    bs_of_cst: "3",
    sp_hpc: invoice.practitioner.hpcsa,
    oti: line.oti || "A",
  };
  transactionInfo.push(baseLine);


  // --- Modifier lines (tp="3") for this base line ---
  for (let j = 0; j < modifiers.length; j++) {
    const modifier = modifiers[j];
    const rateChange =
      typeof modifier === "object" ? Number(modifier.rateChange ?? 0) : 0;
    const modCode =
      typeof modifier === "object" ? modifier.code : String(modifier ?? "");
    const isPositive = rateChange > 0;

    // suffix: 0 -> "a", 1 -> "b", 2 -> "c", ...
    const suffix = String.fromCharCode("a".charCodeAt(0) + j);
    const modifierLineId = `${baseLineId}${suffix}`; // "1a", "1b", "2a", ...

    transactionInfo.push({
      lin_num: modifierLineId,   // <-- "1a", "1b", "1c", ...
      pay_adv: "P",
      tp: "3",
      cdg_set: "02",
      auth_nbr: lineAuthNumber,
      descr: lineDescription,
      process_tp: "01",
      tar_cd: lineTariffCode,
      qty: "1",
      units: lineQuantity,
      unit_tp: "06",
      st_dt,
      st_tm,
      end_dt,
      end_tm,
      cd1: cd1Array, // same ICD coding as base
      cd2: cd2Array,
      tp_ind: "01",
      bhf_nbr: practiceInfo.sp_bhf,
      hpc_nbr: invoice.practitioner.hpcsa,
      tar_ind: "03",
      price: isPositive ? rateChange : 0,
      gross: isPositive ? rateChange : 0,
      bs_of_cst: "3",
      sp_hpc: invoice.practitioner.hpcsa,
      modtp: "3",
      mod: modCode,
      oti: line.oti || "A",
    });
  }

  // Only now move on to the next base line number
  baseLineNumber++;
}

console.log("Prepared claim data:", {
  practiceInfo,
  medicalAidInfo,
  patientInfo,
  transactionInfo,
  financialInfo,
  caseInfo,
  IODInfo,
});

  try {
    const response = await submitMedikreditClaim(
      practiceInfo,
      medicalAidInfo,
      patientInfo,
      transactionInfo,
      financialInfo,
      caseInfo,
      IODInfo,
    );

    console.log("Claim Submission Data:", {
      practiceInfo,
      medicalAidInfo,
      patientInfo,
      transactionInfo,
      financialInfo,
      caseInfo,
      IODInfo,
    });

    console.log("✅ Claim Response:", response);
    return response;
  } catch (error) {
    console.error("❌ Claim Submission Failed:", error);
    throw error;
  }
}
